import { useCallback, useEffect, useState } from "react";

import { api, type Page } from "./api";

/** Loads an ADR-0008 list endpoint with search, sort and "load more". */
export function usePaged<T>(path: string, opts: { sort?: string; limit?: number; extra?: Record<string, string> } = {}) {
  const [q, setQ] = useState("");
  const [sort, setSort] = useState(opts.sort ?? "");
  const [items, setItems] = useState<T[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const limit = opts.limit ?? 50;
  const extraKey = JSON.stringify(opts.extra ?? {});

  const fetchPage = useCallback(
    async (offset: number) => {
      setLoading(true);
      setError(null);
      try {
        const page = await api<Page<T>>(path, {
          query: { limit, offset, sort: sort || undefined, q: q || undefined, ...JSON.parse(extraKey) },
        });
        setItems((prev) => (offset === 0 ? page.items : [...prev, ...page.items]));
        setTotal(page.total);
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setLoading(false);
      }
    },
    [path, limit, sort, q, extraKey],
  );

  useEffect(() => {
    const t = setTimeout(() => fetchPage(0), q ? 250 : 0);
    return () => clearTimeout(t);
  }, [fetchPage, q]);

  return {
    items,
    total,
    loading,
    error,
    q,
    setQ,
    sort,
    setSort,
    hasMore: items.length < total,
    loadMore: () => fetchPage(items.length),
    reload: () => fetchPage(0),
    setItems,
  };
}
